import { Link } from 'react-router-dom'

import { SectionLabel } from '@/components/px/SectionLabel'
import { shortDay } from '@/demo/content/myBoard'
import {
  PHASES,
  agentById,
  personById,
  projectById,
  type WorkPackage,
  type WorkPackageStatus,
} from '@/demo/data'
import { cn } from '@/lib/utils'

/**
 * The read-only work-package board (spec §9 Q3 — no drag, no drop, no
 * `workPackageMoved` act, on no initiative).
 *
 * Column order is `WORK_PACKAGE_STATUSES`, passed in by the caller so no
 * component re-declares it. EVERY column renders, always: a status with no
 * cards in the current scope shows a dashed empty panel rather than
 * disappearing (AC-2.5) — on the client board that emptiness is the point.
 *
 * Each card is a `<Link>`, never a button, so Cmd+click opens a new tab
 * (AC-2.6). Nothing here types a figure, name or date.
 */

export interface KanbanBoardProps {
  workPackages: readonly WorkPackage[]
  columns: readonly WorkPackageStatus[]
  columnLabels: Readonly<Record<WorkPackageStatus, string>>
  emptyLabel: string
  /** Show the engagement + delivery phase on each card (multi-project boards). */
  showProject?: boolean
  /** Show the assignee, or "needs a person" — a people surface cannot read without it. */
  showAssignee?: boolean
  needsPersonLabel?: string
  internalLabel?: string
  ariaLabel: string
}


export function KanbanBoard({
  workPackages,
  columns,
  columnLabels,
  emptyLabel,
  showProject = false,
  showAssignee = false,
  needsPersonLabel = 'Needs a person',
  internalLabel = 'internal',
  ariaLabel,
}: KanbanBoardProps) {
  return (
    <section aria-label={ariaLabel} className="grid grid-cols-5 items-start gap-3">
      {columns.map((status) => {
        const cards = workPackages.filter((w) => w.status === status)
        return (
          <div key={status} className="flex min-w-0 flex-col" data-testid={`kanban-column-${status}`}>
            <div className="flex items-center justify-between px-1 pb-2">
              <SectionLabel>{columnLabels[status]}</SectionLabel>
              <span
                className={cn(
                  'v-figure text-xs tabular-nums text-muted-foreground',
                  status === 'blocked' &&
                    cards.length > 0 &&
                    'rounded-full bg-blocked-bg px-1.5 font-semibold text-blocked'
                )}
              >
                {cards.length}
              </span>
            </div>
            <div className="flex flex-1 flex-col gap-2 rounded-lg border border-border bg-muted p-2">
              {cards.length === 0 ? (
                <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                  {emptyLabel}
                </p>
              ) : (
                cards.map((card) => (
                  <Card
                    key={card.id}
                    card={card}
                    showProject={showProject}
                    showAssignee={showAssignee}
                    needsPersonLabel={needsPersonLabel}
                    internalLabel={internalLabel}
                  />
                ))
              )}
            </div>
          </div>
        )
      })}
    </section>
  )
}

function Card({
  card,
  showProject,
  showAssignee,
  needsPersonLabel,
  internalLabel,
}: {
  card: WorkPackage
  showProject: boolean
  showAssignee: boolean
  needsPersonLabel: string
  internalLabel: string
}) {
  const project = projectById(card.projectId)
  const phase = PHASES.find((p) => p.key === card.phase)
  const agent = card.agentAssistedBy ? agentById(card.agentAssistedBy) : null
  const assignee = card.assigneeId ? personById(card.assigneeId) : null

  return (
    <Link
      to={`/project/${card.projectId}/build`}
      data-wp={card.id}
      className="block rounded-md border border-border bg-card p-3 hover:border-ring"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[0.8125rem] font-medium leading-snug text-foreground">{card.title}</span>
        {!card.clientVisible && (
          <span className="shrink-0 rounded-full bg-neutral-bg px-1.5 py-0.5 text-[0.625rem] font-medium text-neutral">
            {internalLabel}
          </span>
        )}
      </div>
      {showProject && (
        <div className="mt-1.5 truncate text-xs text-muted-foreground">
          {project.client} · {phase?.name}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="v-figure tabular-nums">
          {card.hoursSpent} / {card.hoursPlanned} h
        </span>
        {card.dueDate && <span className="v-figure tabular-nums">Due {shortDay(card.dueDate)}</span>}
      </div>
      {(agent || showAssignee) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {agent && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent px-1.5 py-0.5 text-[0.625rem] font-medium text-accent-foreground">
              {agent.name}
            </span>
          )}
          {showAssignee &&
            (assignee ? (
              <span className="inline-flex items-center gap-1 text-[0.6875rem] text-ink-soft">
                <span
                  aria-hidden
                  className="flex h-4 w-4 items-center justify-center rounded-full bg-people-bg text-[0.5625rem] font-semibold text-people-ink"
                >
                  {assignee.initial}
                </span>
                {assignee.name}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-review-bg px-2 py-0.5 text-xs font-medium text-review">
                {needsPersonLabel}
              </span>
            ))}
        </div>
      )}
    </Link>
  )
}
